import React,{useEffect,useState} from "react";
import {Link} from "react-router-dom";
import {api} from "@/lib/api";
import {useAuth} from "@/auth";
import {toast} from "sonner";

const editableFields=[
 ["pc_type","PC Type"],["pc_date","PC Date"],["customer_name","Customer Name"],["customer_address","Customer Address"],
 ["contact","Contact Person / Mobile"],["gst_number","GST Number"],["container_type","Type of Container"],
 ["external_size","External Size"],["internal_size","Internal Size"],["inside_wall","Inside Finish - Vertical Walls"],
 ["inside_roof","Inside Finish - Roof"],["outside_finish","Outside Finish"],["base","Base"],["core","Core"],
 ["rear_door","Rear Door"],["side_door","Side Door"],["hatch_door","Hatch Door"],["insulation","Insulation"],
 ["floor","Floor"],["vehicle_model","Vehicle Model"],["reefer","Reefer"],["price_details","Price Details"],
 ["payment_details","Payment Details"],["delivery_time","Delivery Time"],["special_customization","Special Customization"]
];

export default function WordPCImportPage(){
 const {user}=useAuth();
 const [file,setFile]=useState(null),[busy,setBusy]=useState(false),[stage,setStage]=useState(null),[fields,setFields]=useState({});
 const [recent,setRecent]=useState([]);
 const allowed=user?.role==="admin"||user?.role==="assistant"||user?.permissions?.confirm_pc;
 const loadRecent=()=>api.get("/word-import/pc/recent").then(r=>setRecent(r.data||[])).catch(()=>{});
 useEffect(()=>{if(allowed)loadRecent()},[allowed]);
 if(!allowed)return <div className="rounded-2xl border border-slate-200 bg-white p-6">Create PC permission required.</div>;

 const stageFile=async()=>{
   if(!file)return toast.error("Choose a Word .docx file");
   const fd=new FormData();fd.append("file",file);
   setBusy(true);
   try{
     const {data}=await api.post("/word-import/pc/stage",fd,{headers:{"Content-Type":"multipart/form-data"}});
     setStage(data);setFields(data.fields||{});toast.success("Word document read into FreshCare for review");loadRecent();
   }catch(e){toast.error(e.response?.data?.detail||"Could not read Word document")}
   finally{setBusy(false)}
 };
 const openStage=async id=>{
   setBusy(true);
   try{const {data}=await api.get("/word-import/pc/"+id);setStage(data);setFields(data.fields||{})}
   catch(e){toast.error(e.response?.data?.detail||"Could not open import")}
   finally{setBusy(false)}
 };
 const resolve=async action=>{
   if(!stage?.id)return;
   setBusy(true);
   try{
    await api.post("/word-import/pc/"+stage.id+"/resolve",{action});
    if(action==="reject"){toast.success("Import rejected");setStage(null)}
    else{toast.success("Numbering conflict cleared; the current next PC will be used")}
    loadRecent();
    if(action!=="reject"){const {data}=await api.get("/word-import/pc/"+stage.id);setStage(data);setFields(data.fields||{})}
   }catch(e){toast.error(e.response?.data?.detail||"Could not resolve import")}
   finally{setBusy(false)}
 };
 const commit=async()=>{
   if(!stage?.id)return;
   if(stage.state!=="ready")return toast.error("This import is not ready");
   if(!window.confirm("Create this PC in FreshCare using the reviewed data?"))return;
   setBusy(true);
   try{
     const {data}=await api.post("/word-import/pc/"+stage.id+"/commit",{fields});
     toast.success("PC "+data.pc_number+" imported successfully");
     setStage({...stage,state:"imported",final_pc_number:Number(data.pc_number),stored_filename:data.stored_filename,filename_changed:data.filename_changed});
     loadRecent();
   }catch(e){toast.error(e.response?.data?.detail||"Import failed")}
   finally{setBusy(false)}
 };
 const set=(k,v)=>setFields(old=>({...old,[k]:v}));
 const statusText=s=>s==="ready"?"Ready to import":s==="admin_review"?"Admin clearance required":s==="imported"?"Imported":s==="rejected"?"Rejected":s||"Unknown";
 return <div className="max-w-5xl mx-auto pb-20 space-y-5">
   <div><Link to="/pcs" className="text-sm text-blue-600 font-medium">← Back to PCs</Link>
    <h1 className="mt-2 text-3xl font-semibold text-slate-950">Import PC from Word</h1>
    <p className="mt-1 text-sm text-slate-500">Upload a manually prepared custom PC. FreshCare extracts the document into structured PC data before anything is saved.</p></div>

   <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 shadow-sm">
    <div><b className="text-sm">Word document</b><p className="text-xs text-slate-500 mt-1">Only .docx files. Uploading does not create a PC until the review is finalized.</p></div>
    <input type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={e=>setFile(e.target.files?.[0]||null)}
      className="block w-full rounded-xl border border-slate-200 p-2 text-sm"/>
    <button disabled={busy||!file} onClick={stageFile} className="h-11 w-full rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-50">{busy?"Reading document…":"Read Word PC into FreshCare"}</button>
   </section>

   {stage&&<section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5 shadow-sm">
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div><h2 className="text-xl font-semibold">Import review</h2><p className="text-xs text-slate-500 mt-1">{stage.original_filename}</p></div>
      <div className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700">{statusText(stage.state)}</div>
    </div>

    <div className="grid sm:grid-cols-3 gap-3 text-sm">
      <div className="rounded-xl border border-slate-200 p-3"><span className="text-xs text-slate-500">Document PC</span><div className="font-semibold mt-1">{stage.original_pc_number??"Not detected"}</div></div>
      <div className="rounded-xl border border-slate-200 p-3"><span className="text-xs text-slate-500">Next PC at upload</span><div className="font-semibold mt-1">{stage.next_pc_at_stage??"—"}</div></div>
      <div className="rounded-xl border border-slate-200 p-3"><span className="text-xs text-slate-500">Stored filename</span><div className="font-semibold mt-1 break-all">{stage.stored_filename||stage.original_filename}</div></div>
    </div>

    {!!stage.warnings?.length&&<div className="rounded-xl border border-slate-300 bg-slate-50 p-4 space-y-2">
      {stage.warnings.map((w,i)=><div key={i} className="text-sm text-slate-800 flex gap-2"><span aria-hidden="true">⚠</span><span>{w}</span></div>)}
    </div>}

    {stage.state==="admin_review"&&<div className="rounded-xl border border-slate-300 p-4">
      <b className="text-sm">Import paused</b>
      <p className="text-sm text-slate-600 mt-1">Nothing has been added to the PC database. An administrator must clear the numbering issue first.</p>
      {user?.role==="admin"&&<div className="mt-3 flex flex-wrap gap-2">
        <button disabled={busy} onClick={()=>resolve("renumber_next")} className="h-10 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white disabled:opacity-50">Clear using current next PC</button>
        <button disabled={busy} onClick={()=>resolve("reject")} className="h-10 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 disabled:opacity-50">Reject import</button>
      </div>}
    </div>}

    <div>
      <h3 className="font-semibold">Extracted FreshCare data</h3>
      <p className="text-xs text-slate-500 mt-1">Review or correct any field before final import. Custom values are kept exactly as entered.</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
       {editableFields.map(([k,label])=><label key={k} className={(k==="special_customization"||k==="customer_address"||k==="reefer")?"sm:col-span-2 text-sm":"text-sm"}>
        <span className="font-medium">{label}</span>
        {(k==="special_customization"||k==="customer_address"||k==="reefer")?
          <textarea value={fields[k]||""} onChange={e=>set(k,e.target.value)} className="mt-1 min-h-20 w-full rounded-xl border border-slate-200 p-3"/>:
          <input value={fields[k]||""} onChange={e=>set(k,e.target.value)} className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-3"/>}
       </label>)}
      </div>
    </div>

    {stage.state==="ready"&&<button disabled={busy} onClick={commit} className="h-12 w-full rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-50">{busy?"Importing…":"Create PC in FreshCare"}</button>}
    {stage.state==="imported"&&<div className="rounded-xl border border-slate-300 p-4 text-sm"><b>Imported as PC {stage.final_pc_number}</b><div className="mt-1 text-slate-600">Stored Word filename: {stage.stored_filename}</div></div>}
   </section>}

   <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <h2 className="text-lg font-semibold">{user?.role==="admin"?"Recent Word imports":"My recent Word imports"}</h2>
    <div className="mt-3 space-y-2">{recent.map(r=><button key={r.id} onClick={()=>openStage(r.id)} disabled={busy}
      className="w-full rounded-xl border border-slate-200 p-3 text-left hover:bg-slate-50">
      <div className="flex justify-between gap-3"><span className="font-semibold text-sm">PC {r.final_pc_number??r.original_pc_number??"?"} · {r.original_filename}</span><span className="text-xs text-slate-500">{statusText(r.state)}</span></div>
      <div className="text-xs text-slate-500 mt-1">{r.created_by_name||""} · {(r.created_at||"").slice(0,16).replace("T"," ")}</div>
    </button>)}{!recent.length&&<p className="text-sm text-slate-500">No Word imports yet.</p>}</div>
   </section>
 </div>;
}